import { JobPostingJsonLd } from "next-seo";
import { Place, UnitTextType } from "next-seo/lib/jsonld/jobPosting";
import { VacancyProps } from "storyblok/components";

import { getDescriptionHtml } from "helpers/transform/vacancyDescription";

type VacancyStructuredDataProps = {
    vacancy: VacancyProps;
};

function dutchToEnglishDate(dateStr: string) {
    const monthMap = {
        april: "April",
        augustus: "August",
        december: "December",
        februari: "February",
        januari: "January",
        juli: "July",
        juni: "June",
        maart: "March",
        mei: "May",
        november: "November",
        oktober: "October",
        september: "September"
    };

    let result = dateStr;
    for (const [dutch, english] of Object.entries(monthMap)) {
        if (dateStr.toLowerCase().includes(dutch)) {
            // Replace the Dutch month with English month (preserving case)
            const regex = new RegExp(dutch, "i");
            result = dateStr.replace(regex, english);
            break;
        }
    }

    return result;
}

function getEmploymentType(vacancy: VacancyProps) {
    switch (vacancy.employmentType) {
        case "Secondment":
            return "FULL_TIME";
        case "Temporary":
            return "TEMPORARY";
        case "Nul uren":
        default:
            return "OTHER";
    }
}

function getJobLocation(vacancy: VacancyProps): Place {
    const { city, postcode, country, isZZP, client } = vacancy;

    const addressLocality = isZZP ? client?.town : city;
    const postalCode = isZZP ? client?.zipcode : postcode;
    const streetAddress = vacancy.client?.street;
    return {
        addressCountry: country ?? "Nederland",
        addressLocality: addressLocality ?? undefined,
        postalCode: postalCode ?? undefined,
        streetAddress: streetAddress ?? undefined
    };
}

function getBaseSalary(vacancy: VacancyProps) {
    const { salary } = vacancy;
    const salaryValue =
        salary?.max && salary?.min
            ? ([Number(salary.min), Number(salary.max)] as [number, number])
            : null;

    return salaryValue && salary?.period
        ? {
              currency: "EUR",
              unitText: salary?.period?.toLocaleUpperCase() as UnitTextType,
              value: salaryValue
          }
        : undefined;
}

function getHiringOrganization(vacancy: VacancyProps) {
    const { client, isZZP } = vacancy;

    const domain =
        typeof window !== "undefined"
            ? window?.location?.hostname
            : "https://leadhealthcare.nl";

    return {
        name: (isZZP ? client.company_name : client.accountName) ?? "",
        sameAs: domain
    };
}

function getDatePosted(vacancy: VacancyProps) {
    const { created_at, start_date, isZZP } = vacancy;

    if (isZZP && start_date) {
        const startDate = new Date(dutchToEnglishDate(start_date));

        const isValidDate = !isNaN(startDate?.getTime());

        if (isValidDate && startDate) {
            return startDate?.toISOString() ?? "";
        }

        return "";
    }

    return created_at || "";
}

function getValidThrough(vacancy: VacancyProps) {
    const { closed_at, end_date, isZZP } = vacancy;

    if (isZZP && end_date) {
        const endDate = new Date(dutchToEnglishDate(end_date));
        const isValidDate = !isNaN(endDate?.getTime());

        if (isValidDate && endDate) {
            return endDate?.toISOString() ?? "";
        }

        return "";
    }

    return closed_at || "";
}

export function VacancyStructuredData({ vacancy }: VacancyStructuredDataProps) {
    return (
        <JobPostingJsonLd
            datePosted={getDatePosted(vacancy)}
            description={getDescriptionHtml(vacancy) ?? ""}
            hiringOrganization={getHiringOrganization(vacancy)}
            title={vacancy.title ?? ""}
            validThrough={getValidThrough(vacancy)}
            baseSalary={getBaseSalary(vacancy)}
            employmentType={getEmploymentType(vacancy)}
            jobLocation={getJobLocation(vacancy)}
        />
    );
}
